import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { Store } from "../../../services/store.service";
import "../../../../assets/libs/ckeditor5/build/translations/es";
import "../../../../assets/libs/ckeditor5/build/translations/fr";
import "../../../../assets/libs/ckeditor5/build/translations/ar";
import "../../../../assets/libs/ckeditor5/build/translations/et";
import { ClassicEditor } from "../../../../assets/libs/ckeditor5";

export interface PdfSettingsSelectOption {
  id: string;
  labelKey: string;
}

export interface PdfSettingsToggleOption {
  id: string;
  labelKey: string;
}

const GRID_UNITS = 12;
/** Maximum number of header/footer columns (matches grid units). */
const MAX_COLUMNS = 12;
const DEFAULT_COLUMN_COUNT = 3;
/** Horizontal space per separator (width + horizontal margins) — keep in sync with SCSS. */
const SEPARATOR_PX = 16;

interface ColumnResizeState {
  section: "header" | "footer";
  separatorIndex: number;
  startClientX: number;
  startWidths: number[];
  rowElement: HTMLElement;
  captureTarget: HTMLElement;
  pointerId: number;
}

@Component({
  selector: "app-print-document-pdf-settings",
  templateUrl: "./print-document-pdf-settings.component.html",
  styleUrls: ["./print-document-pdf-settings.component.scss"],
})
export class PrintDocumentPdfSettingsComponent implements OnChanges, OnDestroy {

  @Input() public pageProperties: any;
  @Output() public pagePropertiesChange: EventEmitter<any> = new EventEmitter<any>();

  private hydrating = false;
  private lastEmitted: any = null;

  public readonly maxColumns = MAX_COLUMNS;

  public readonly pageSizeOptions: PdfSettingsSelectOption[] = [
    { id: "letter", labelKey: "printDoc.pdfSettings.pageSize.letter" },
    { id: "legal", labelKey: "printDoc.pdfSettings.pageSize.legal" },
    { id: "a4", labelKey: "printDoc.pdfSettings.pageSize.a4Default" },
    { id: "a5", labelKey: "printDoc.pdfSettings.pageSize.a5" },
    { id: "custom", labelKey: "printDoc.pdfSettings.pageSize.custom" },
  ];

  public readonly marginOptions: PdfSettingsSelectOption[] = [
    { id: "0mm", labelKey: "printDoc.pdfSettings.margin.none" },
    { id: "6mm", labelKey: "printDoc.pdfSettings.margin.small" },
    { id: "12mm", labelKey: "printDoc.pdfSettings.margin.mediumDefault" },
    { id: "18mm", labelKey: "printDoc.pdfSettings.margin.large" },
    { id: "24mm", labelKey: "printDoc.pdfSettings.margin.huge" },
  ];

  public readonly orientationOptions: PdfSettingsToggleOption[] = [
    { id: "portrait", labelKey: "printDoc.pdfSettings.orientation.portrait" },
    { id: "landscape", labelKey: "printDoc.pdfSettings.orientation.landscape" },
  ];

  public readonly watermarkVerticalOptions: PdfSettingsToggleOption[] = [
    { id: "top", labelKey: "printDoc.pdfSettings.watermark.vertical.top" },
    { id: "center", labelKey: "printDoc.pdfSettings.watermark.vertical.center" },
    { id: "bottom", labelKey: "printDoc.pdfSettings.watermark.vertical.bottom" },
  ];

  public readonly watermarkHorizontalOptions: PdfSettingsToggleOption[] = [
    { id: "left", labelKey: "printDoc.pdfSettings.watermark.horizontal.left" },
    { id: "center", labelKey: "printDoc.pdfSettings.watermark.horizontal.center" },
    { id: "right", labelKey: "printDoc.pdfSettings.watermark.horizontal.right" },
  ];

  public pageSize = "a4";
  /** Width × height (e.g. cm) when `pageSize` is `custom`. */
  public customPageSize = "";
  public orientation = "portrait";
  public marginTop = "12mm";
  public marginBottom = "12mm";
  public marginLeft = "12mm";
  public marginRight = "12mm";
  public watermarkVertical = "center";
  public watermarkHorizontal = "center";
  public watermarkOpacity = 1;
  public watermarkContent = "";
  public cornerTopLeft = "";
  public cornerTopRight = "";
  public cornerBottomLeft = "";
  public cornerBottomRight = "";

  public showHeader = false;
  public showFooter = false;
  /** Width units per column; sum must equal GRID_UNITS. */
  public headerWidths: number[] = [];
  public headerContents: string[] = [];
  public footerWidths: number[] = [];
  public footerContents: string[] = [];

  public showPageCount = false;

  public Editor = ClassicEditor;
  public config = {
    toolbar: {
      items: [
        "heading",
        "|",
        "fontFamily",
        "fontSize",
        "fontColor",
        "underline",
        "bold",
        "italic",
        "link",
        "bulletedList",
        "numberedList",
        "|",
        "alignment",
        "outdent",
        "indent",
        "|",
        "imageUpload",
        "blockQuote",
        "insertTable",
        "undo",
        "redo",
        "|",
        "horizontalLine",
        "|",
        "removeFormat",
        "htmlEmbed",
      ],
    },
    language: this.store.state.common.lang,
    image: {
      toolbar: [
        "imageTextAlternative",
        "imageStyle:full",
        "imageStyle:side",
        "linkImage",
      ],
    },
    table: {
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableCellProperties",
        "tableProperties",
      ],
    },
  };

  private resizeState: ColumnResizeState | null = null;

  private readonly boundResizeMove = (e: PointerEvent) => this.onResizePointerMove(e);
  private readonly boundResizeEnd = (e: PointerEvent) => this.onResizePointerEnd(e);

  constructor(public store: Store) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.pageProperties && changes.pageProperties.currentValue !== this.lastEmitted) {
      this.hydrateFromInput();
    }
  }

  public ngOnDestroy(): void {
    this.teardownResizeListeners();
  }

  public emitChange(): void {
    if (this.hydrating) {
      return;
    }
    const snapshot = this.buildSnapshot();
    this.lastEmitted = snapshot;
    this.pagePropertiesChange.emit(snapshot);
  }

  private buildSnapshot(): any {
    return {
      pageSize: this.pageSize,
      customPageSize: this.customPageSize,
      orientation: this.orientation,
      marginTop: this.marginTop,
      marginBottom: this.marginBottom,
      marginLeft: this.marginLeft,
      marginRight: this.marginRight,
      watermarkVertical: this.watermarkVertical,
      watermarkHorizontal: this.watermarkHorizontal,
      watermarkOpacity: this.watermarkOpacity,
      watermarkContent: this.watermarkContent,
      cornerTopLeft: this.cornerTopLeft,
      cornerTopRight: this.cornerTopRight,
      cornerBottomLeft: this.cornerBottomLeft,
      cornerBottomRight: this.cornerBottomRight,
      showHeader: this.showHeader,
      showFooter: this.showFooter,
      headerWidths: [...this.headerWidths],
      headerContents: [...this.headerContents],
      footerWidths: [...this.footerWidths],
      footerContents: [...this.footerContents],
      showPageCount: this.showPageCount,
    };
  }

  private hydrateFromInput(): void {
    const p = this.pageProperties;
    if (!p || typeof p !== "object") {
      return;
    }
    this.hydrating = true;
    try {
      if (p.pageSize !== undefined) { this.pageSize = p.pageSize; }
      if (p.customPageSize !== undefined) { this.customPageSize = p.customPageSize; }
      if (p.orientation !== undefined) { this.orientation = p.orientation; }
      if (p.marginTop !== undefined) { this.marginTop = p.marginTop; }
      if (p.marginBottom !== undefined) { this.marginBottom = p.marginBottom; }
      if (p.marginLeft !== undefined) { this.marginLeft = p.marginLeft; }
      if (p.marginRight !== undefined) { this.marginRight = p.marginRight; }
      if (p.watermarkVertical !== undefined) { this.watermarkVertical = p.watermarkVertical; }
      if (p.watermarkHorizontal !== undefined) { this.watermarkHorizontal = p.watermarkHorizontal; }
      if (p.watermarkOpacity !== undefined && p.watermarkOpacity !== null && !Number.isNaN(Number(p.watermarkOpacity))) {
        this.watermarkOpacity = Number(p.watermarkOpacity);
      }
      if (p.watermarkContent !== undefined) { this.watermarkContent = p.watermarkContent; }
      if (p.cornerTopLeft !== undefined) { this.cornerTopLeft = p.cornerTopLeft; }
      if (p.cornerTopRight !== undefined) { this.cornerTopRight = p.cornerTopRight; }
      if (p.cornerBottomLeft !== undefined) { this.cornerBottomLeft = p.cornerBottomLeft; }
      if (p.cornerBottomRight !== undefined) { this.cornerBottomRight = p.cornerBottomRight; }
      if (p.showHeader !== undefined) { this.showHeader = p.showHeader; }
      if (p.showFooter !== undefined) { this.showFooter = p.showFooter; }
      if (Array.isArray(p.headerWidths)) { this.headerWidths = [...p.headerWidths]; }
      if (Array.isArray(p.headerContents)) { this.headerContents = [...p.headerContents]; }
      if (Array.isArray(p.footerWidths)) { this.footerWidths = [...p.footerWidths]; }
      if (Array.isArray(p.footerContents)) { this.footerContents = [...p.footerContents]; }
      if (p.showPageCount !== undefined) { this.showPageCount = p.showPageCount; }
    } finally {
      this.hydrating = false;
    }
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public columnFlexBasis(widthUnits: number): string {
    return `${widthUnits} 1 0%`;
  }

  public onShowHeaderChange(enabled: boolean): void {
    if (enabled && this.headerWidths.length === 0) {
      this.headerWidths = this.distributeEqualUnits(DEFAULT_COLUMN_COUNT);
      this.headerContents = new Array(DEFAULT_COLUMN_COUNT).fill("");
    }
    this.emitChange();
  }

  public onShowFooterChange(enabled: boolean): void {
    if (enabled && this.footerWidths.length === 0) {
      this.footerWidths = this.distributeEqualUnits(DEFAULT_COLUMN_COUNT);
      this.footerContents = new Array(DEFAULT_COLUMN_COUNT).fill("");
    }
    this.emitChange();
  }

  public addHeaderColumn(): void {
    if (this.headerWidths.length >= MAX_COLUMNS) {
      return;
    }
    const n = this.headerWidths.length + 1;
    this.headerWidths = this.distributeEqualUnits(n);
    this.headerContents.push("");
    this.emitChange();
  }

  public addFooterColumn(): void {
    if (this.footerWidths.length >= MAX_COLUMNS) {
      return;
    }
    const n = this.footerWidths.length + 1;
    this.footerWidths = this.distributeEqualUnits(n);
    this.footerContents.push("");
    this.emitChange();
  }

  public removeHeaderColumn(index: number): void {
    if (this.headerWidths.length <= 1) {
      return;
    }
    this.headerContents.splice(index, 1);
    const n = this.headerWidths.length - 1;
    this.headerWidths = this.distributeEqualUnits(n);
    this.emitChange();
  }

  public removeFooterColumn(index: number): void {
    if (this.footerWidths.length <= 1) {
      return;
    }
    this.footerContents.splice(index, 1);
    const n = this.footerWidths.length - 1;
    this.footerWidths = this.distributeEqualUnits(n);
    this.emitChange();
  }

  public onSeparatorPointerDown(event: PointerEvent, section: "header" | "footer", separatorIndex: number): void {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    const rowEl = target.closest(".pdf-column-flex") as HTMLElement | null;
    if (!rowEl) {
      return;
    }
    const widths = section === "header" ? this.headerWidths : this.footerWidths;
    if (separatorIndex < 0 || separatorIndex >= widths.length - 1) {
      return;
    }
    this.resizeState = {
      section,
      separatorIndex,
      startClientX: event.clientX,
      startWidths: [...widths],
      rowElement: rowEl,
      captureTarget: target,
      pointerId: event.pointerId,
    };
    target.setPointerCapture(event.pointerId);
    window.addEventListener("pointermove", this.boundResizeMove);
    window.addEventListener("pointerup", this.boundResizeEnd);
    window.addEventListener("pointercancel", this.boundResizeEnd);
  }

  private onResizePointerMove(event: PointerEvent): void {
    if (!this.resizeState) {
      return;
    }
    const rowWidth = this.resizeState.rowElement.offsetWidth;
    if (rowWidth <= 0) {
      return;
    }
    const n = this.resizeState.startWidths.length;
    const sepCount = Math.max(0, n - 1);
    const availableWidth = Math.max(0, rowWidth - sepCount * SEPARATOR_PX);
    const unitPx = availableWidth / GRID_UNITS;
    const deltaUnits = Math.round((event.clientX - this.resizeState.startClientX) / unitPx);
    const i = this.resizeState.separatorIndex;
    const w = [...this.resizeState.startWidths];
    const maxRight = w[i + 1] - 1;
    const maxLeft = -(w[i] - 1);
    const d = Math.max(maxLeft, Math.min(deltaUnits, maxRight));
    w[i] += d;
    w[i + 1] -= d;
    if (this.resizeState.section === "header") {
      this.headerWidths = w;
    } else {
      this.footerWidths = w;
    }
  }

  private onResizePointerEnd(_event: PointerEvent): void {
    const wasResizing = this.resizeState !== null;
    if (this.resizeState) {
      const el = this.resizeState.captureTarget;
      try {
        el.releasePointerCapture(this.resizeState.pointerId);
      } catch {
        // ignore
      }
    }
    this.resizeState = null;
    this.teardownResizeListeners();
    if (wasResizing) {
      this.emitChange();
    }
  }

  private teardownResizeListeners(): void {
    window.removeEventListener("pointermove", this.boundResizeMove);
    window.removeEventListener("pointerup", this.boundResizeEnd);
    window.removeEventListener("pointercancel", this.boundResizeEnd);
  }

  private distributeEqualUnits(count: number): number[] {
    if (count <= 0) {
      return [];
    }
    const base = Math.floor(GRID_UNITS / count);
    const rem = GRID_UNITS % count;
    const widths = new Array(count).fill(base);
    for (let i = 0; i < rem; i++) {
      widths[i]++;
    }
    return widths;
  }
}
